import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';

import AdminToolbar from './admin_toolbar';
import AssistantHeader from './assistant_header';
import AssistantLauncher from './assistant_launcher';
import ConversationBody from './conversation_body';
import MessageInput from './message_input';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { speechToTextSupported, useSpeechToText } from './audio/use_speech_to_text';
import { textToSpeechSupported, useTextToSpeech } from './audio/use_text_to_speech';
import { OPEN_ASSISTANT_EVENT, type OpenAssistantOptions } from './events';
import { applyStreamFrame, updateActionStatuses } from './stream';
import { useAssistant } from './use_assistant';
import { useChatResume } from './use_chat_resume';
import { useTools } from './tools/context';

import type { ActionVerdict, Agent, AssistantAction, InputMode, Message, Paths } from './types';

import './styles.scss';

interface Props {
  agents: Agent[];
  currentUser: {
    admin: boolean;
  };
  paths: Paths;
}

// Docks the panel to the bottom of the navbar (anchor-name set in
// partials/_header.html.slim), clamped to the viewport top once the navbar scrolls away.
const PANEL_TOP = 'calc(max(0px, anchor(--main-nav bottom)) + 0.75rem)';
const panelTop = typeof CSS !== 'undefined' && CSS.supports('top', PANEL_TOP) ? PANEL_TOP : '0.75rem';

export default function AiAssistantSidebar({ agents, currentUser, paths }: Props) {
  const {
    agent, ask, loadChat, loading, model, resetChat, resolveAction, setAgent, setChatId, setModel,
  } = useAssistant(agents, paths);
  const { setToolCalls } = useTools();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showAdminDetails, setShowAdminDetails] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const conversationStartedRef = useRef(false);

  const ttsSupported = textToSpeechSupported();
  const tts = useTextToSpeech();
  const speakTurnRef = useRef(false);

  function addAttachments(newAttachments: File[]) {
    setAttachments(attachments => [...attachments, ...newAttachments]);
  }

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDropAccepted: addAttachments,
  });

  const sendMessage = async (prompt: string, opts?: { inputMode?: InputMode, speak?: boolean }) => {
    const trimmed = prompt.trim();
    if (!trimmed || loading) return;

    const messageAttachments = attachments;
    setAttachments([]);

    conversationStartedRef.current = true;
    tts.cancel();
    speakTurnRef.current = ttsSupported && !!opts?.speak;

    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: trimmed,
        attachments: messageAttachments.map(attachment => ({ name: attachment.name })),
      },
      {
        role: 'assistant',
        content: '',
        isStreaming: true,
      },
    ]);

    let spoken = '';
    const { toolCalls } = await ask({
      attachments: messageAttachments,
      inputMode: opts?.inputMode ?? 'text',
      prompt: trimmed,
      onFrame(frame) {
        setMessages(prev => applyStreamFrame(prev, frame));

        if (!speakTurnRef.current) return;
        if (frame.type === 'delta') {
          spoken += frame.text;
          tts.feed(spoken);
        } else if (frame.type === 'done') {
          tts.finish(frame.content);
        } else if (frame.type === 'error') {
          tts.cancel();
        }
      },
    });

    setToolCalls(toolCalls);
  };

  const dropTrailingPlaceholder = (prev: Message[]): Message[] => {
    const last = prev[prev.length - 1];
    return last?.role === 'assistant' && !last.content && last.isStreaming ? prev.slice(0, -1) : prev;
  };

  const handleResolveAction = async (action: AssistantAction, verdict: ActionVerdict) => {
    if (loading) return;

    conversationStartedRef.current = true;
    tts.cancel();
    speakTurnRef.current = false;

    const optimistic: AssistantAction = { ...action, status: verdict === 'apply' ? 'applying' : 'rejected' };
    setMessages(prev => [
      ...updateActionStatuses(prev, [optimistic]),
      { role: 'assistant', content: '', isStreaming: true },
    ]);

    let resolved = false;
    const { toolCalls, error } = await resolveAction({
      action,
      verdict,
      onResolved: (status, resumed) => {
        resolved = true;
        setMessages((prev) => {
          const updated = updateActionStatuses(prev, [{ ...action, status }]);
          return resumed ? updated : dropTrailingPlaceholder(updated);
        });
      },
      onFrame: frame => setMessages(prev => applyStreamFrame(prev, frame)),
    });

    if (error && !resolved) {
      setMessages(prev => updateActionStatuses(prev, [action]));
    }
    setToolCalls(toolCalls);
  };

  // The window listener below is subscribed once, so it calls sendMessage through a
  // ref that is repointed every render to avoid acting on stale state
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const sttSupported = speechToTextSupported();
  const { cancel: cancelRecording, recording, toggle: toggleRecording } = useSpeechToText({
    onTranscript: setInput,
    onFinish: (transcript: string) => {
      setInput('');
      void sendMessage(transcript, { inputMode: 'voice', speak: true });
    },
  });

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OpenAssistantOptions>).detail;

      setOpen(true);
      if (detail?.prompt) void sendMessageRef.current(detail.prompt);
    };

    window.addEventListener(OPEN_ASSISTANT_EVENT, handler);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, handler);
  }, []);

  // Restore the most recent chat the first time the panel opens, unless a send
  // already began (see conversationStartedRef in sendMessage).
  const resuming = useChatResume({
    open,
    loadChat,
    hasStarted: () => conversationStartedRef.current,
    onResume: ({ chatId, messages: resumedMessages }) => {
      setMessages(resumedMessages);
      setChatId(chatId);
    },
  });

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages, loading, open]);

  const submit = () => {
    void sendMessage(input);
    setInput('');
  };

  const startNewChat = () => {
    conversationStartedRef.current = true;
    tts.cancel();
    cancelRecording();
    setMessages([]);
    setInput('');
    setAttachments([]);
    setToolCalls();
    void resetChat();
  };

  const handleMicClick = () => {
    // Prime speech synthesis inside this gesture so iOS lets us speak the reply later.
    if (!recording) tts.prime();
    toggleRecording();
  };

  if (!open) {
    return <AssistantLauncher onClick={() => setOpen(true)} />;
  }

  return (
    // TODO: move this to a mounted component next to the main layout
    <Card
      className="position-fixed d-flex flex-column rounded-3 shadow-lg"
      style={{ bottom: '0.75rem', right: '0.75rem', top: panelTop, width: '360px', zIndex: 1045 }}
      {...getRootProps()}
    >
      {isDragActive && (
        <div
          className={classNames(
            'position-absolute top-0 start-0 bottom-0 end-0',
            'd-flex align-items-center justify-content-center',
            'bg-light border border-dashed rounded-3 text-muted',
          )}
          style={{ pointerEvents: 'none', zIndex: 1 }}
        >
          Drop files to attach
        </div>
      )}

      <AssistantHeader
        isAdmin={currentUser.admin}
        newChatDisabled={loading || resuming || messages.length === 0}
        onClose={() => {
          tts.cancel();
          setOpen(false);
        }}
        onNewChat={startNewChat}
        onToggleAdminDetails={() => setShowAdminDetails(prev => !prev)}
      />

      {showAdminDetails &&
        <AdminToolbar
          agent={agent}
          agents={agents}
          messages={messages}
          model={model}
          models={agent?.models || []}
          onAgentChange={setAgent}
          onModelChange={setModel}
        />
      }

      <Card.Body className="ai-assistant-chat flex-grow-1 overflow-auto" ref={bodyRef}>
        <ConversationBody
          messages={messages}
          resolveDisabled={loading || resuming}
          resuming={resuming}
          showAdminDetails={showAdminDetails}
          speaking={tts.speaking}
          onResolveAction={(action, verdict) => void handleResolveAction(action, verdict)}
          onSelectStarter={prompt => void sendMessage(prompt)}
          onStopSpeaking={() => tts.cancel()}
        />
      </Card.Body>

      <Card.Footer>
        <MessageInput
          attachments={attachments}
          input={input}
          loading={loading || resuming}
          recording={recording}
          sttSupported={sttSupported}
          onAddAttachments={addAttachments}
          onInputChange={setInput}
          onMicClick={handleMicClick}
          onRemoveAttachment={index => setAttachments(attachments => attachments.filter((_, i) => i !== index))}
          onSubmit={submit}
        />
      </Card.Footer>
    </Card>
  );
}
